/**
 * The message composer (TMMAPP-0142 items 2, 4, 4a): the one composer for every assistant session
 * (private sessions now; shared sessions and Help & feedback reuse it). A labelled multi-line box,
 * the caller's controls (Fast / Deep), dictation, and Send.
 *
 * - **Send** is the caller's: it may be held (an answer is running) while the box stays usable for
 *   typing. The box clears only when the caller reports the message was accepted. Enter sends,
 *   Shift+Enter is a new line.
 * - **Two sends in a shared session** (TMMAPP-0164 item 15, from TMMAPP-0142 U3 item 16's Ask the
 *   assistant): at the box's end, **Send to members** (the paper plane, first and the default: Enter)
 *   then **Send to assistant** (the AI symbol on the AI pill's surface; Ctrl/Cmd+Enter). Each tooltip
 *   says both shortcuts. Neither is ever disabled: an empty box or a held action simply sends
 *   nothing. While an answer runs, Send to assistant shows it (`aria-busy`, a spinner) and Send to
 *   members still sends. When the caller can't ask (`ask.explain`), Send to assistant explains
 *   instead of sending, and the text stays. From `md` its label ("Send to assistant") shows on hover
 *   or keyboard focus, its width animating (none with reduced motion, which shows the label). The
 *   box reserves the expanded width (an invisible copy sizes the actions), so the text area never
 *   reflows and the label never covers it (TMMAPP-0167 item 4). At `xs`, and on any screen with no
 *   hover (TMMAPP-0166), it shows its short label ("Assistant") always; at `xs` both buttons are
 *   44 px.
 * - **Dictation** (4a): a mic button where the browser has speech recognition, hidden where it
 *   doesn't. Interim words show provisionally at the cursor and final words are inserted, for the
 *   person to review; it **never sends by itself**. The button toggles (`aria-pressed`, a visible
 *   "Listening…" and a polite announcement); Send, Escape, leaving the composer, or the browser
 *   ending recognition stop it. The first use shows a one-line notice with OK (then listening
 *   starts) and Cancel; the acknowledgement is remembered under `tmm_dictation_notice_ack`, and a
 *   storage failure only means the notice shows again. A refused microphone shows its message at
 *   the composer, and the button stays.
 * - **Attachments** (TMMAPP-0142 U5, item 25a), when the caller passes them (`useAttachments`): a
 *   paperclip (its tooltip is the attach line), plus drop and paste onto the composer. The files
 *   show above the box; the send waits while one isn't ready, and the accepted message takes the
 *   files it carried. While a message is sending, nothing can be attached or removed (K2). Only a
 *   drag that carries files is taken as a drop; dragged text goes into the box (K5). Files alone
 *   can't be sent: with files attached and no text, the composer says "Add a message to send your
 *   files." (J5).
 * - **A prefilled text** (Ask about this, TMMAPP-0151 §B) starts in the box with focus on it, for
 *   the person to edit and send; it never sends.
 * - **The v2 look** (TMMAPP-0153 item 8, visual contract v2 §11): prompt chips (pills, 13) above
 *   the field; **a chip fills the box, it never sends** (into an empty box it is the text, else it
 *   joins the end). The field is radius 16 with the `lineStrong` boundary on `surface`, holding the
 *   40 px Send button. Fast / Deep, attach and dictation sit on the row under it.
 *   The box keeps its accessible name ("Message"); the placeholder is the session kind's.
 */
import {
  useEffect,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type DragEvent,
  type FocusEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LiveRegion } from '@/components/LiveRegion';
import { AiSparkles } from '@/components/AiPill';
import { AttachIcon, MicIcon, SendIcon } from '@/components/icons';
import { useLocale } from '@/i18n/useLocale';
import { PILL_RADIUS } from '@/theme';
import { resolveTokens } from '@/theme/theme';
import { ATTACHMENT_ACCEPT } from './attachmentRules';
import { AttachmentTray } from './AttachmentTray';
import type { AttachmentsController } from './useAttachments';
import { useDictation } from './useDictation';

export const DICTATION_NOTICE_KEY = 'tmm_dictation_notice_ack';

/** The remembered acknowledgement; false when storage can't be read. */
function noticeAcknowledged(): boolean {
  try {
    return window.localStorage.getItem(DICTATION_NOTICE_KEY) === '1';
  } catch {
    return false;
  }
}

function rememberNotice(): void {
  try {
    window.localStorage.setItem(DICTATION_NOTICE_KEY, '1');
  } catch {
    /* the notice simply shows again next time */
  }
}

/** Joins dictated words to the text before them with one space where needed. */
function joinWords(before: string, words: string): string {
  const clean = words.trim();
  if (clean.length === 0) return before;
  if (before.length === 0 || /\s$/.test(before)) return before + clean;
  return `${before} ${clean}`;
}

const MESSAGE_MAX_LENGTH = 4000;

/**
 * The sends' tooltips wait a moment on hover: a pointer on its way to click doesn't flash them open
 * (and position them) on every send (TMMAPP-0164); a lingering hover or focus still shows them.
 */
const SEND_TIP_DELAY_MS = 600;

/** Send to assistant's chrome: the AI pill's surface, the send button's height and radius. */
const AI_BUTTON_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: { xs: 44, md: 40 },
  minWidth: { xs: 44, md: 40 },
  px: '10px',
  borderRadius: '12px',
  whiteSpace: 'nowrap',
  // A <button> doesn't inherit the face by default; the reserving copy (a span) does.
  font: 'inherit',
} as const;

// The body face, so the invisible copy that reserves the width measures the same as the button.
const AI_LABEL_TEXT_SX = {
  fontFamily: 'inherit',
  fontSize: 14,
  fontWeight: 600,
  lineHeight: '20px',
  letterSpacing: 'normal',
} as const;
/** The label's space from the icon, when it shows. */
const AI_LABEL_GAP = '6px';

interface ComposerProps {
  /** The box's accessible name. */
  label: string;
  placeholder?: string | undefined;
  /** Send's label ("Send"). */
  sendLabel: string;
  /** Send is held (an answer is running); typing stays open. */
  sendHeld: boolean;
  /** Sends the text and the attached files' ids; resolves true when accepted (the box then clears). */
  onSend: (text: string, attachmentIds: number[]) => Promise<boolean>;
  /** Controls beside Send (Fast / Deep). */
  controls?: ReactNode;
  /** A message at the composer (a refusal, the busy line). */
  notice?: ReactNode;
  /** Send to assistant beside Send to members (a shared session); Send is then the members'. */
  ask?: ComposerAsk | undefined;
  /** A line under the actions ("Paid from your AI balance"). */
  hint?: ReactNode;
  /** The files attached for this message; no paperclip without them. */
  attachments?: AttachmentsController | undefined;
  /** Text the box starts with (focused); the person still chooses to send it. */
  initialText?: string | undefined;
  /** Prompt chips above the box: each fills it, never sends (TMMAPP-0153 item 8). */
  chips?: readonly string[] | undefined;
}

export interface ComposerAsk {
  /** Its accessible name, always ("Send to assistant"); the label it expands to from `md`. */
  label: string;
  /** The label beside the icon at `xs` ("Assistant"). */
  shortLabel: string;
  /** An answer is running: asking is held (its running state), sending isn't. */
  held: boolean;
  /** Resolves true when accepted (the box then clears). */
  onAsk: (text: string, attachmentIds: number[]) => Promise<boolean>;
  /** The caller can't ask: activating explains why instead (nothing sent, the text stays). */
  explain?: (() => void) | undefined;
}

/** Ctrl, or the Command key on an Apple device (the shortcut's modifier). */
function appleDevice(): boolean {
  const platform =
    typeof navigator === 'undefined'
      ? ''
      : ((navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData
          ?.platform ?? navigator.platform);
  return /mac|iphone|ipad/i.test(platform);
}

export function Composer({
  label,
  placeholder,
  sendLabel,
  sendHeld,
  onSend,
  controls,
  notice,
  ask,
  hint,
  attachments,
  initialText,
  chips = [],
}: ComposerProps) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const locale = useLocale();
  const inputId = useId();
  const [text, setText] = useState(initialText ?? '');
  const [sending, setSending] = useState<'send' | 'ask' | null>(null);
  const [showNotice, setShowNotice] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  // A prefilled box takes focus once, at the end of its text, for the person to edit.
  const prefilled = useRef((initialText ?? '').length > 0);
  useEffect(() => {
    const node = inputRef.current;
    if (!prefilled.current || node === null) return;
    prefilled.current = false;
    node.focus();
    node.setSelectionRange(node.value.length, node.value.length);
  }, []);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const attachRef = useRef<HTMLButtonElement | null>(null);
  const [dragging, setDragging] = useState(false);
  // A file still uploading, failed or expired holds both actions.
  const filesHeld = attachments?.held ?? false;
  // While listening: the text either side of the cursor when it started; final words join
  // `before`, the interim phrase shows between the two. The ref is the source of truth (the
  // recognition events are native, so a final result and the end can both arrive before React
  // re-renders); the state mirrors it for rendering.
  const anchorRef = useRef<{ before: string; after: string } | null>(null);
  const [anchor, setAnchorState] = useState<{ before: string; after: string } | null>(null);
  const [interim, setInterim] = useState('');
  const setAnchor = (next: { before: string; after: string } | null) => {
    anchorRef.current = next;
    setAnchorState(next);
  };

  const dictation = useDictation(locale, {
    onInterim: setInterim,
    onFinal: (words) => {
      const current = anchorRef.current;
      if (current !== null) {
        setAnchor({ before: joinWords(current.before, words), after: current.after });
      }
    },
    onEnd: () => commitDictation(),
  });

  const shown =
    anchor === null
      ? text
      : (interim.trim().length > 0 ? joinWords(anchor.before, interim) : anchor.before) +
        anchor.after;

  /** The dictated words become the box's text; the provisional phrase is dropped. */
  function commitDictation() {
    const current = anchorRef.current;
    if (current !== null) setText(current.before + current.after);
    setAnchor(null);
    setInterim('');
  }

  function startListening() {
    const input = inputRef.current;
    const cursor = input?.selectionStart ?? text.length;
    setAnchor({ before: text.slice(0, cursor), after: text.slice(cursor) });
    setInterim('');
    // A start that fails leaves no insertion point behind, so the box never freezes (H4).
    if (!dictation.start()) {
      setAnchor(null);
      return;
    }
    setAnnouncement(t('assistant.dictation.listening'));
  }

  function stopListening() {
    if (!dictation.listening) return;
    dictation.stop();
    commitDictation();
    setAnnouncement(t('assistant.dictation.stopped'));
  }

  function toggleMic() {
    if (dictation.listening) {
      stopListening();
      return;
    }
    if (!noticeAcknowledged()) {
      setShowNotice(true);
      return;
    }
    startListening();
  }

  async function send(how: 'send' | 'ask' = 'send') {
    // The caller can't ask: Send to assistant explains, whatever is in the box (item 14).
    if (how === 'ask' && ask?.explain !== undefined) {
      ask.explain();
      return;
    }
    const current = anchorRef.current;
    const message = (current === null ? text : current.before + current.after).trim();
    stopListening();
    const held = (how === 'ask' ? (ask?.held ?? true) : sendHeld) || filesHeld;
    if (message.length === 0 || held || sending !== null) return;
    // An upload the server has swept by now shows as expired, and the message waits.
    const ids = attachments === undefined ? [] : attachments.prepareSend();
    if (ids === null) return;
    setSending(how);
    try {
      const accepted =
        how === 'ask' && ask !== undefined
          ? await ask.onAsk(message, ids)
          : await onSend(message, ids);
      if (accepted) {
        setText('');
        attachments?.clearSent(ids);
      }
    } finally {
      setSending(null);
    }
  }

  /** A chip's words go into the box, for the person to edit and send; nothing is sent. */
  function fillWith(words: string) {
    if (dictation.listening) {
      dictation.stop();
      setAnnouncement(t('assistant.dictation.stopped'));
    }
    const current = anchorRef.current;
    const base = current === null ? text : current.before + current.after;
    setAnchor(null);
    setInterim('');
    const next = base.trim().length === 0 ? words : joinWords(base, words);
    setText(next);
    const node = inputRef.current;
    if (node !== null) {
      node.focus();
      queueMicrotask(() => node.setSelectionRange(next.length, next.length));
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape' && dictation.listening) {
      event.preventDefault();
      stopListening();
    }
  }

  function handleInputKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    // An IME composing (`isComposing`, or `keyCode` 229 where a browser doesn't set it) keeps its
    // Enter (TMMAPP-0166 item 3).
    const composing = event.nativeEvent.isComposing || event.keyCode === 229;
    if (event.key === 'Enter' && !event.shiftKey && !composing) {
      event.preventDefault();
      // Enter sends to the members; Ctrl/Cmd+Enter to the assistant, where there are two sends.
      void send(ask !== undefined && (event.ctrlKey || event.metaKey) ? 'ask' : 'send');
    }
  }

  function handleBlur(event: FocusEvent<HTMLDivElement>) {
    const next = event.relatedTarget;
    if (next instanceof Node && rootRef.current?.contains(next)) return;
    stopListening();
  }

  const empty = (anchor === null ? text : anchor.before + anchor.after).trim().length === 0;
  const shortcuts =
    ask === undefined
      ? null
      : t('assistant.composer.shortcuts', {
          mod: appleDevice() ? t('assistant.composer.modCmd') : t('assistant.composer.modCtrl'),
        });
  /** A send's tooltip: its name, then both shortcuts on their own line. */
  const sendTip = (name: string) => (
    <Box component="span" sx={{ display: 'block' }}>
      <Box component="span" sx={{ display: 'block', fontWeight: 600 }}>
        {name}
      </Box>
      {shortcuts}
    </Box>
  );
  const sendSx = {
    width: { xs: ask === undefined ? 40 : 44, md: 40 },
    height: { xs: ask === undefined ? 40 : 44, md: 40 },
    borderRadius: '12px',
    bgcolor: tokens.brand,
    color: tokens.brandInk,
    '&:hover': { bgcolor: tokens.brandHover },
    '&.Mui-disabled': { bgcolor: tokens.hover, color: tokens.text2 },
    '&.Mui-focusVisible': {
      outline: `2px solid ${tokens.focusRing}`,
      outlineOffset: 2,
    },
  } as const;
  const asking = ask !== undefined && (ask.held || sending === 'ask');

  // Nothing is attached or removed while a message is sending (K2).
  const filesLocked = sending !== null;

  function attachFiles(files: FileList | null | undefined) {
    if (attachments === undefined || files === null || files === undefined) return;
    if (filesLocked) return;
    const list = Array.from(files);
    if (list.length > 0) attachments.add(list);
  }

  function handlePaste(event: ClipboardEvent<HTMLDivElement>) {
    if (attachments === undefined || event.clipboardData.files.length === 0) return;
    // Text with a picture of it (cells copied from a spreadsheet, a passage from a document) pastes
    // as text; only a paste of files alone attaches.
    if (event.clipboardData.getData('text/plain').length > 0) return;
    // A pasted picture or file attaches.
    event.preventDefault();
    attachFiles(event.clipboardData.files);
  }

  function handleDragOver(event: DragEvent<HTMLDivElement>) {
    if (attachments === undefined || !event.dataTransfer.types.includes('Files')) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    if (!dragging) setDragging(true);
  }

  function handleDragLeave(event: DragEvent<HTMLDivElement>) {
    const next = event.relatedTarget;
    if (next instanceof Node && rootRef.current?.contains(next)) return;
    setDragging(false);
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    // Dragged text drops into the box as usual; only files attach (K5).
    if (attachments === undefined || !event.dataTransfer.types.includes('Files')) return;
    event.preventDefault();
    setDragging(false);
    attachFiles(event.dataTransfer.files);
  }

  return (
    <Box
      ref={rootRef}
      data-composer=""
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      data-dragging={dragging ? '' : undefined}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        borderRadius: 1.5,
        outline: dragging ? `2px dashed ${tokens.link}` : 'none',
        outlineOffset: 4,
      }}
    >
      {notice}
      {showNotice && (
        <Alert
          severity="info"
          data-dictation-notice=""
          action={
            <Stack direction="row" sx={{ gap: 0.5 }}>
              <Button color="inherit" size="small" onClick={() => setShowNotice(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  rememberNotice();
                  setShowNotice(false);
                  startListening();
                }}
              >
                {t('assistant.dictation.ok')}
              </Button>
            </Stack>
          }
        >
          {t('assistant.dictation.notice')}
        </Alert>
      )}
      {dictation.denied && (
        <Alert severity="warning" data-dictation-denied="">
          {t('assistant.dictation.denied')}
        </Alert>
      )}
      {attachments !== undefined && (
        <AttachmentTray
          attachments={attachments}
          locked={filesLocked}
          onEmpty={() => attachRef.current?.focus()}
        />
      )}
      {chips.length > 0 && (
        <Box
          role="group"
          aria-label={t('assistant.chips.label')}
          data-prompt-chips=""
          sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}
        >
          {chips.map((chip) => (
            <ButtonBase
              key={chip}
              onClick={() => fillWith(chip)}
              data-prompt-chip=""
              sx={{
                p: '7px 12px',
                borderRadius: `${PILL_RADIUS}px`,
                border: '1px solid',
                borderColor: tokens.lineStrong,
                bgcolor: tokens.surface,
                color: tokens.text,
                fontSize: 13,
                lineHeight: '18px',
                textAlign: 'start',
                '&:hover': { borderColor: tokens.brandBoundary },
                '&.Mui-focusVisible': {
                  outline: `2px solid ${tokens.focusRing}`,
                  outlineOffset: 2,
                },
              }}
            >
              {chip}
            </ButtonBase>
          ))}
        </Box>
      )}
      <TextField
        id={inputId}
        inputRef={inputRef}
        onPaste={handlePaste}
        placeholder={placeholder}
        value={shown}
        onChange={(event) => {
          // Typing while listening stops it; the provisional words already in the box stay as
          // text (H12). A stale insertion point (listening ended without its commit) is cleared too.
          if (dictation.listening) {
            dictation.stop();
            setAnnouncement(t('assistant.dictation.stopped'));
          }
          if (anchorRef.current !== null) {
            setAnchor(null);
            setInterim('');
          }
          setText(event.target.value);
        }}
        onKeyDown={handleInputKeyDown}
        multiline
        minRows={1}
        maxRows={8}
        fullWidth
        sx={{
          '& .MuiOutlinedInput-root': {
            alignItems: 'flex-end',
            gap: 1,
            p: '6px',
            paddingInlineStart: 2,
            borderRadius: '16px',
            bgcolor: tokens.surface,
            fontSize: 15,
          },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: tokens.lineStrong },
          '& .MuiOutlinedInput-input': { py: 1 },
        }}
        slotProps={{
          htmlInput: { maxLength: MESSAGE_MAX_LENGTH, 'aria-label': label },
          input: {
            endAdornment: (
              <InputAdornment position="end" sx={{ height: 'auto', maxHeight: 'none', m: 0 }}>
                {ask === undefined ? (
                  <IconButton
                    aria-label={sendLabel}
                    loading={sending === 'send'}
                    disabled={empty || sendHeld || filesHeld || sending === 'ask'}
                    onClick={() => void send()}
                    data-send=""
                    sx={sendSx}
                  >
                    <SendIcon />
                  </IconButton>
                ) : (
                  // The two sends (item 15). From `md` an invisible copy, Send to assistant
                  // expanded, sizes the cell, so its hover expansion never reflows or covers the
                  // text area (TMMAPP-0167 item 4: the G7 ruling keeps the reserve at `md`).
                  <Box
                    data-send-actions=""
                    sx={{
                      display: 'grid',
                      '& > *': { gridArea: '1 / 1' },
                      justifyItems: 'end',
                      alignItems: 'end',
                    }}
                  >
                    <Box
                      aria-hidden
                      data-send-reserve=""
                      sx={{
                        display: { xs: 'none', md: 'flex' },
                        visibility: 'hidden',
                        gap: 0.75,
                        pointerEvents: 'none',
                        // No hover: the full label never shows, so nothing is reserved for it.
                        '@media (hover: none)': { display: 'none' },
                      }}
                    >
                      <Box sx={{ width: 40, height: 40 }} />
                      <Box component="span" sx={AI_BUTTON_SX}>
                        <AiSparkles size={20} />
                        <Box
                          component="span"
                          sx={{ ...AI_LABEL_TEXT_SX, marginInlineStart: AI_LABEL_GAP }}
                        >
                          {ask.label}
                        </Box>
                      </Box>
                    </Box>
                    <Stack direction="row" sx={{ gap: 0.75, alignItems: 'center' }}>
                      <Tooltip
                        title={sendTip(sendLabel)}
                        describeChild
                        enterDelay={SEND_TIP_DELAY_MS}
                      >
                        <IconButton
                          aria-label={sendLabel}
                          aria-busy={sending === 'send' || undefined}
                          onClick={() => void send()}
                          data-send=""
                          sx={sendSx}
                        >
                          {sending === 'send' ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            <SendIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={sendTip(ask.label)}
                        describeChild
                        enterDelay={SEND_TIP_DELAY_MS}
                      >
                        <ButtonBase
                          aria-label={ask.label}
                          aria-busy={asking || undefined}
                          onClick={() => void send('ask')}
                          data-send-assistant=""
                          data-running={asking ? '' : undefined}
                          sx={{
                            ...AI_BUTTON_SX,
                            bgcolor: tokens.surface2,
                            color: tokens.text2,
                            '&.Mui-focusVisible': {
                              outline: `2px solid ${tokens.focusRing}`,
                              outlineOffset: 2,
                            },
                            // From `md` the label shows on hover or keyboard focus.
                            '& [data-ai-label-full]': {
                              display: { xs: 'none', md: 'inline-block' },
                              maxWidth: 0,
                              marginInlineStart: 0,
                              overflow: 'hidden',
                              transition: 'max-width 160ms ease, margin-inline-start 160ms ease',
                            },
                            '&:hover [data-ai-label-full], &.Mui-focusVisible [data-ai-label-full]':
                              { maxWidth: 240, marginInlineStart: AI_LABEL_GAP },
                            '& [data-ai-label-short]': {
                              display: { xs: 'inline', md: 'none' },
                              marginInlineStart: AI_LABEL_GAP,
                            },
                            '@media (prefers-reduced-motion: reduce)': {
                              '& [data-ai-label-full]': {
                                transition: 'none',
                                maxWidth: 'none',
                                marginInlineStart: AI_LABEL_GAP,
                              },
                            },
                            // A touch screen has no hover at any width: the short label shows, as
                            // at `xs` (TMMAPP-0166 item 3).
                            '@media (hover: none)': {
                              '& [data-ai-label-full]': { display: 'none' },
                              '& [data-ai-label-short]': { display: 'inline' },
                            },
                          }}
                        >
                          {asking ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            <AiSparkles size={20} />
                          )}
                          <Box
                            component="span"
                            aria-hidden
                            data-ai-label-short=""
                            sx={AI_LABEL_TEXT_SX}
                          >
                            {ask.shortLabel}
                          </Box>
                          <Box
                            component="span"
                            aria-hidden
                            data-ai-label-full=""
                            sx={AI_LABEL_TEXT_SX}
                          >
                            {ask.label}
                          </Box>
                        </ButtonBase>
                      </Tooltip>
                    </Stack>
                  </Box>
                )}
              </InputAdornment>
            ),
          },
        }}
      />
      <Stack
        direction="row"
        sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'space-between' }}
      >
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {controls}
          {attachments !== undefined && (
            <>
              <Tooltip title={t('assistant.attach.line')} describeChild>
                <IconButton
                  ref={attachRef}
                  aria-label={t('assistant.attach.button')}
                  disabled={filesLocked}
                  onClick={() => fileRef.current?.click()}
                  sx={{ color: tokens.text2 }}
                >
                  <AttachIcon />
                </IconButton>
              </Tooltip>
              <Box
                component="input"
                ref={fileRef}
                type="file"
                multiple
                hidden
                accept={ATTACHMENT_ACCEPT}
                data-attach-input=""
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  attachFiles(event.target.files);
                  // The same file can be picked again after it's removed.
                  event.target.value = '';
                }}
              />
            </>
          )}
          {dictation.supported && (
            <Tooltip title={t('assistant.dictation.button')}>
              <IconButton
                aria-label={t('assistant.dictation.button')}
                aria-pressed={dictation.listening}
                onClick={toggleMic}
                sx={
                  dictation.listening
                    ? {
                        bgcolor: tokens.selected,
                        color: tokens.link,
                        '&:hover': { bgcolor: tokens.selected },
                      }
                    : { color: tokens.text2 }
                }
              >
                <MicIcon />
              </IconButton>
            </Tooltip>
          )}
          {dictation.listening && (
            <Typography
              component="span"
              data-dictation-listening=""
              sx={{ fontSize: 13, color: tokens.link, fontWeight: 500 }}
            >
              {t('assistant.dictation.listening')}
            </Typography>
          )}
        </Stack>
      </Stack>
      {empty && attachments !== undefined && attachments.items.length > 0 && (
        <Typography sx={{ fontSize: 12, color: tokens.text2 }} data-files-need-text="">
          {t('assistant.attach.needsText')}
        </Typography>
      )}
      {hint}
      <LiveRegion text={announcement} />
    </Box>
  );
}
